import { toast } from "react-toastify";

export function formatDateToISOString(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export const formatTimeTo12Hour = (timeString) => {
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours, 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  const formattedHour = hour % 12 || 12;
  return `${formattedHour}:${minutes} ${period}`;
};

export const formatDate = (dateString) => {
  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  return new Date(dateString).toLocaleDateString("en-US", options);
};

export const formatTime = (timeString) => {
  const [hours, minutes] = timeString.split(":");
  const time = new Date();
  time.setHours(parseInt(hours));
  time.setMinutes(parseInt(minutes));
  return time.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
};

export const convertTimeToMinutes = (timeString) => {
  const [hours, minutes] = timeString.split(":").map(Number);
  return hours * 60 + minutes;
};

export const isValidTimeRange = (startTime, endTime) => {
  const startMinutes = convertTimeToMinutes(startTime);
  const endMinutes = convertTimeToMinutes(endTime);
  return startMinutes < endMinutes;
};

//TutorAvailability
const hasOverlappingSlots = (slots, newStartTime, newEndTime, currentIndex) => {
  const newStart = convertTimeToMinutes(newStartTime);
  const newEnd = convertTimeToMinutes(newEndTime);

  return slots.some((slot, index) => {
    if (index === currentIndex) return false;
    const slotStart = convertTimeToMinutes(slot.startTime);
    const slotEnd = convertTimeToMinutes(slot.endTime);
    return (
      (newStart >= slotStart && newStart < slotEnd) ||
      (newEnd > slotStart && newEnd <= slotEnd) ||
      (newStart <= slotStart && newEnd >= slotEnd)
    );
  });
};

export const validateSlots = (slots) => {
  const errors = {};

  slots.forEach((slot, index) => {
    if (!isValidTimeRange(slot.startTime, slot.endTime)) {
      errors[index] = "End time must be after start time";
    } else if (hasOverlappingSlots(slots, slot.startTime, slot.endTime, index)) {
      errors[index] = "Time slot overlaps with another slot";
    }
  });

  return errors;
};

export const getNext7Days = () => {
  const days = [];
  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  for (let i = 0; i < 7; i++) {
    const date = new Date();
    date.setDate(date.getDate() + i);
    days.push({
      date: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      day: dayNames[date.getDay()],
    });
  }
  return days;
};

export const formatCurrency = (amount, currencyCode = "USD", locale = "en-US") => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
  }).format(amount);
};

// Utility function for formatting date and time
export const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// utils/utils.js
export const formatDatePlusTime = (date, time) => {
  if (!date) return "";

  const dateTimeString = time ? `${date} ${time}` : date;
  const parsedDate = new Date(dateTimeString);

  if (isNaN(parsedDate)) return date; 

  return parsedDate.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};



export const parseTime = (baseDate, timeString) => {
  const cleanTime = timeString.includes("T")
    ? timeString.split("T")[1].substring(0, 5)
    : timeString;
  const [hours, minutes] = cleanTime.split(":").map(Number);
  const date = new Date(baseDate);
  date.setHours(hours, minutes, 0, 0);
  return date;
};

export const transformPermissionsFromBackend = (data) => {
  const capitalize = (str) =>
    str
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());

  const buildChildren = (obj) => {
    return Object.entries(obj).map(([key, value]) => {
      const label = capitalize(key);

      if (typeof value === 'object' && value !== null) {
        const children = buildChildren(value);

        return {
          id: key,
          label,
          checked: Object.values(value).every(
            (v) => v === true || (typeof v === 'object' && Object.values(v).every((vv) => vv === true))
          ),
          indeterminate: !Object.values(value).every(
            (v) => v === true || (typeof v === 'object' && Object.values(v).every((vv) => vv === true))
          ) &&
            Object.values(value).some(
              (v) => v === true || (typeof v === 'object' && Object.values(v).some((vv) => vv === true))
            ),
          children,
        };
      } else {
        return {
          id: key,
          label,
          checked: !!value,
          children: [],
        };
      }
    });
  };

  return buildChildren(data);
}

export const transformPermissionsToBackend = (treePermissions) => {
  const result = {};

  for (const item of treePermissions) {
    if (!item.children || item.children.length === 0) {
      result[item.id] = !!item.checked;
    } else {
      const nested = {};
      for (const child of item.children) {
        if (!child.children || child.children.length === 0) {
          nested[child.id] = !!child.checked;
        } else {
          const subNested = {};
          for (const grandChild of child.children) {
            subNested[grandChild.id] = !!grandChild.checked;
          }
          nested[child.id] = subNested;
        }
      }
      result[item.id] = nested;
    }
  }

  return result;
};

export const getErrorMessage = (error) => toast.error(error ? error : "Something went wrong");
